/**
 * Wallet Migration Service — Transfers all data from one Cardano wallet to another
 *
 * Flow:
 * 1. Verify destination wallet is fresh (no existing data)
 * 2. Collect inventory of what will migrate
 * 3. Collect 15 ADA fee — CIP-30 wallet signs + submits tx directly
 * 4. Execute atomic migration via /api/migration server endpoint
 * 5. Handle Quarry membership notifications
 */

import { supabase } from '../supabaseClient';
import { enableWallet } from './ariadne';

const MIGRATION_FEE_LOVELACE = 15_000_000; // 15 ADA
const MIGRATION_FEE_ADA = 15;

// ============================================================
// Address validation — ensure we never send bech32 to the server
// CIP-30 wallets always return hex. If someone pastes addr1...,
// we reject it client-side rather than letting the server deal with it.
// ============================================================

function isHexAddress(addr) {
  return typeof addr === 'string' && /^[0-9a-fA-F]+$/.test(addr) && addr.length >= 56;
}

function validateHexAddress(addr, label) {
  if (!addr) throw new Error(`${label} is required`);
  if (!isHexAddress(addr)) {
    if (addr.startsWith('addr')) {
      throw new Error(`${label} must be a hex address (from wallet API), not bech32 (addr1...). Connect the wallet directly instead of pasting an address.`);
    }
    throw new Error(`${label} is not a valid hex wallet address`);
  }
}

// ============================================================
// Inventory — gather everything tied to the old wallet
// ============================================================

export async function getMigrationInventory(walletAddress) {
  const inventory = {
    users: null,
    authors: [],
    posts: [],
    orgSeats: [],
    organizations: [],
    subscriptions: [],
    socialLinks: [],
    orgInvites: [],
    quarryDisbursements: [],
    entityHandles: [],
    follows: [],
  };

  try {
    // 1. User record
    const { data: user } = await supabase
      .from('users')
      .select('*')
      .eq('wallet_address', walletAddress)
      .single();
    inventory.users = user;

    // 2. Authors
    const { data: authors } = await supabase
      .from('authors')
      .select('*')
      .eq('wallet_address', walletAddress);
    inventory.authors = authors || [];

    // 3. Posts
    const { data: posts } = await supabase
      .from('posts')
      .select('*')
      .eq('wallet_address', walletAddress);
    inventory.posts = posts || [];

    // 4. Org seats
    const { data: seats } = await supabase
      .from('org_seats')
      .select('*')
      .eq('wallet_address', walletAddress);
    inventory.orgSeats = seats || [];

    // Get organization details for seats
    if (inventory.orgSeats.length > 0) {
      const orgIds = [...new Set(inventory.orgSeats.map(s => s.org_id))];
      const { data: orgs } = await supabase
        .from('organizations')
        .select('*')
        .in('id', orgIds);
      inventory.organizations = orgs || [];
    }

    // 5. Subscriptions (as subscriber)
    const { data: subs } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('subscriber_wallet', walletAddress);
    inventory.subscriptions = subs || [];

    // 6. Social links — try wallet_address column (legacy), skip if column doesn't exist
    try {
      const { data: links, error: slError } = await supabase
        .from('social_links')
        .select('*')
        .eq('wallet_address', walletAddress);
      if (!slError) {
        inventory.socialLinks = links || [];
      }
    } catch (e) {
      // Column doesn't exist — entity-based schema, no wallet_address rows to migrate
    }

    // 7. Org invites created by this wallet
    const { data: invites } = await supabase
      .from('org_invites')
      .select('*')
      .eq('created_by', walletAddress);
    inventory.orgInvites = invites || [];

    // 8. Quarry disbursements
    const { data: disbursements } = await supabase
      .from('quarry_disbursements')
      .select('*')
      .eq('recipient_wallet', walletAddress);
    inventory.quarryDisbursements = disbursements || [];

    // 9. Entity handles — tied to author UUIDs, not wallet addresses.
    // They stay valid because author IDs don't change during migration.
    if (inventory.authors.length > 0) {
      const authorIds = inventory.authors.map(a => a.id);
      const { data: handles } = await supabase
        .from('entity_handles')
        .select('*')
        .eq('entity_type', 'individual')
        .in('entity_id', authorIds);
      inventory.entityHandles = handles || [];
    }

    // 10. Follows — tied to entity IDs (author UUIDs), not wallet addresses.
    // Included for display purposes but don't need migration.
    if (inventory.authors.length > 0) {
      const authorIds = inventory.authors.map(a => a.id);
      const { data: followsData } = await supabase
        .from('follows')
        .select('*')
        .eq('follower_type', 'individual')
        .in('follower_id', authorIds);
      inventory.follows = followsData || [];
    }

    return inventory;
  } catch (error) {
    console.error('Error building migration inventory:', error);
    throw new Error('Failed to build migration inventory');
  }
}

// ============================================================
// Validation — ensure destination wallet is fresh
// ============================================================

export async function validateDestinationWallet(newWalletAddress, oldWalletAddress) {
  if (!newWalletAddress || typeof newWalletAddress !== 'string') {
    return { valid: false, reason: 'Invalid wallet address' };
  }

  if (!isHexAddress(newWalletAddress)) {
    return { valid: false, reason: 'Wallet address must be hex format (from CIP-30 API). Got: ' + newWalletAddress.substring(0, 20) + '...' };
  }

  if (newWalletAddress === oldWalletAddress) {
    return { valid: false, reason: 'New wallet address is the same as current wallet' };
  }

  const { data: existingUser } = await supabase
    .from('users')
    .select('wallet_address')
    .eq('wallet_address', newWalletAddress)
    .maybeSingle();

  if (existingUser) {
    return { valid: false, reason: 'Destination wallet already has an account. Migration requires a fresh wallet.' };
  }

  const { data: existingAuthors } = await supabase
    .from('authors')
    .select('id')
    .eq('wallet_address', newWalletAddress)
    .limit(1);

  if (existingAuthors && existingAuthors.length > 0) {
    return { valid: false, reason: 'Destination wallet already has author profiles.' };
  }

  const { data: existingSeats } = await supabase
    .from('org_seats')
    .select('id')
    .eq('wallet_address', newWalletAddress)
    .limit(1);

  if (existingSeats && existingSeats.length > 0) {
    return { valid: false, reason: 'Destination wallet already has organization memberships.' };
  }

  return { valid: true, reason: null };
}

// ============================================================
// Treasury address — fetch from server
// ============================================================

async function getTreasuryAddress() {
  const res = await fetch('/api/migration', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'get-treasury-address' }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to fetch treasury address');
  }

  return res.json();
}

// ============================================================
// Fee Payment — 15 ADA sent to Quarry treasury via CIP-30
// ============================================================

export async function payMigrationFee(walletName) {
  const wallet = await enableWallet(walletName);

  const { treasuryAddress } = await getTreasuryAddress();
  if (!treasuryAddress) {
    throw new Error('Could not retrieve treasury address');
  }

  const usedAddresses = await wallet.getUsedAddresses();
  if (!usedAddresses || usedAddresses.length === 0) {
    throw new Error('WALLET_NO_ADDRESSES');
  }
  const senderAddress = usedAddresses[0];

  // Build unsigned transaction via server (has Blockfrost access for UTxO lookup)
  const buildRes = await fetch('/api/migration-build-tx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      senderAddress,
      treasuryAddress,
      amountLovelace: MIGRATION_FEE_LOVELACE,
    }),
  });

  if (!buildRes.ok) {
    const body = await buildRes.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to build fee transaction');
  }

  const { unsignedTx } = await buildRes.json();

  // Sign with wallet
  let witnessSetHex;
  try {
    witnessSetHex = await wallet.signTx(unsignedTx, true);
  } catch (err) {
    throw new Error('USER_REJECTED');
  }

  // Assemble signed transaction
  const signedTxHex = assembleTx(unsignedTx, witnessSetHex);

  // Submit
  let txHash;
  try {
    txHash = await wallet.submitTx(signedTxHex);
  } catch (err) {
    console.error('Wallet submitTx failed:', err);
    console.error('submitTx error code:', err?.code);
    console.error('submitTx error info:', err?.info);
    console.error('Signed tx length:', signedTxHex?.length, 'chars');
    throw new Error('Transaction submission failed: ' + (err.info || err.message || JSON.stringify(err)));
  }

  return {
    txHash,
    amount: MIGRATION_FEE_ADA,
    timestamp: Date.now(),
  };
}

// ============================================================
// Balance check
// ============================================================

export async function checkMigrationBalance(walletName) {
  try {
    const wallet = await enableWallet(walletName);
    const balanceCbor = await wallet.getBalance();

    if (typeof balanceCbor === 'string') {
      const bytes = hexToBytes(balanceCbor);
      const lovelace = parseCborUint(bytes);
      if (lovelace !== null) {
        const ada = lovelace / 1_000_000;
        return {
          sufficient: lovelace >= MIGRATION_FEE_LOVELACE + 2_000_000,
          balanceAda: ada,
          requiredAda: MIGRATION_FEE_ADA,
        };
      }
    }
    return null;
  } catch (err) {
    console.error('Balance check failed:', err);
    return null;
  }
}

// ============================================================
// Execute Migration — calls server endpoint
// Validates addresses are hex BEFORE sending
// ============================================================

export async function executeMigration(oldWallet, newWallet, feeTxHash) {
  // Client-side validation — catch bech32 before it hits the server
  validateHexAddress(oldWallet, 'Source wallet');
  validateHexAddress(newWallet, 'Destination wallet');

  if (!feeTxHash || feeTxHash.length < 40) {
    throw new Error('Invalid transaction hash');
  }

  const res = await fetch('/api/migration', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'execute',
      oldWallet,
      newWallet,
      txHash: feeTxHash,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Migration failed');
  }

  return data;
}

// ============================================================
// Quarry notifications
// ============================================================

export async function notifyQuarryMembers(orgSeats, oldWallet, newWallet) {
  const notifications = [];

  for (const seat of orgSeats) {
    const { data: allSeats } = await supabase
      .from('org_seats')
      .select('wallet_address, role, author_id')
      .eq('org_id', seat.org_id)
      .neq('wallet_address', newWallet);

    const { data: org } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', seat.org_id)
      .single();

    if (allSeats && allSeats.length > 0) {
      notifications.push({
        orgId: seat.org_id,
        orgName: org?.name || 'Unknown Organization',
        memberCount: allSeats.length,
        role: seat.role,
      });
    }
  }

  return notifications;
}

// ============================================================
// Error messages
// ============================================================

export function getMigrationErrorMessage(error) {
  const msg = error?.message || String(error);

  if (msg.includes('USER_REJECTED')) {
    return 'Transaction cancelled. You must approve the 15 ADA fee to proceed with migration.';
  }
  if (msg.includes('WALLET_NO_ADDRESSES')) {
    return 'Wallet has no addresses. Please ensure your wallet has been used before.';
  }
  if (msg.includes('INSUFFICIENT') || msg.includes('insufficient')) {
    return 'Insufficient ADA. Migration requires at least 17 ADA (15 ADA fee + network costs).';
  }
  if (msg.includes('Payment not confirmed')) {
    return 'Fee payment not yet confirmed on-chain. Please wait 30 seconds and try again.';
  }
  if (msg.includes('must be a hex address')) {
    return msg;
  }
  if (msg.includes('Invalid source address') || msg.includes('Invalid destination address')) {
    return msg;
  }
  if (msg.includes('already has')) {
    return msg;
  }
  if (msg.includes('Migration failed')) {
    return msg;
  }
  return 'Migration failed: ' + msg;
}

export { MIGRATION_FEE_ADA, MIGRATION_FEE_LOVELACE };

// ============================================================
// CBOR / Hex utilities
// ============================================================

function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes) {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Assemble a signed Cardano transaction from unsigned tx CBOR + witness set CBOR.
 * Cardano tx = CBOR array: [body, witnesses, isValid, auxiliaryData]
 * We replace the empty witnesses (index 1) with the signed witness set.
 */
function assembleTx(unsignedTxHex, witnessSetHex) {
  const txBytes = hexToBytes(unsignedTxHex);
  const witnessBytes = hexToBytes(witnessSetHex);

  if (txBytes[0] !== 0x84) {
    console.warn('Unexpected CBOR prefix:', txBytes[0].toString(16));
  }

  let offset = 1;
  const bodyStart = offset;
  offset = skipCborItem(txBytes, offset);
  const bodyEnd = offset;

  offset = skipCborItem(txBytes, offset); // skip old witnesses
  const witnessEnd = offset;

  const tailBytes = txBytes.slice(witnessEnd);

  const result = new Uint8Array(1 + (bodyEnd - bodyStart) + witnessBytes.length + tailBytes.length);
  result[0] = 0x84;
  result.set(txBytes.slice(bodyStart, bodyEnd), 1);
  result.set(witnessBytes, 1 + (bodyEnd - bodyStart));
  result.set(tailBytes, 1 + (bodyEnd - bodyStart) + witnessBytes.length);

  return bytesToHex(result);
}

function skipCborItem(bytes, offset) {
  const first = bytes[offset];
  const major = first >> 5;
  const additional = first & 0x1f;
  offset++;

  let argValue;
  if (additional < 24) {
    argValue = additional;
  } else if (additional === 24) {
    argValue = bytes[offset]; offset += 1;
  } else if (additional === 25) {
    argValue = (bytes[offset] << 8) | bytes[offset + 1]; offset += 2;
  } else if (additional === 26) {
    argValue = (bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3];
    argValue = argValue >>> 0;
    offset += 4;
  } else if (additional === 27) {
    argValue = 0;
    for (let i = 0; i < 8; i++) {
      argValue = argValue * 256 + bytes[offset + i];
    }
    offset += 8;
  } else if (additional === 31) {
    if (major === 2 || major === 3) {
      while (bytes[offset] !== 0xff) { offset = skipCborItem(bytes, offset); }
      offset++;
    } else if (major === 4) {
      while (bytes[offset] !== 0xff) { offset = skipCborItem(bytes, offset); }
      offset++;
    } else if (major === 5) {
      while (bytes[offset] !== 0xff) {
        offset = skipCborItem(bytes, offset);
        offset = skipCborItem(bytes, offset);
      }
      offset++;
    }
    return offset;
  } else {
    return offset;
  }

  switch (major) {
    case 0: case 1: break;
    case 2: case 3: offset += argValue; break;
    case 4:
      for (let i = 0; i < argValue; i++) { offset = skipCborItem(bytes, offset); }
      break;
    case 5:
      for (let i = 0; i < argValue; i++) {
        offset = skipCborItem(bytes, offset);
        offset = skipCborItem(bytes, offset);
      }
      break;
    case 6: offset = skipCborItem(bytes, offset); break;
    case 7: break;
  }

  return offset;
}

function parseCborUint(bytes) {
  const first = bytes[0];
  const major = first >> 5;
  const additional = first & 0x1f;
  if (major !== 0) {
    if (major === 4) return parseCborUint(bytes.slice(1));
    return null;
  }
  if (additional < 24) return additional;
  if (additional === 24) return bytes[1];
  if (additional === 25) return (bytes[1] << 8) | bytes[2];
  if (additional === 26) return (bytes[1] << 24) | (bytes[2] << 16) | (bytes[3] << 8) | bytes[4];
  if (additional === 27) {
    // eslint-disable-next-line no-undef
    let val = BigInt(0);
    for (let i = 1; i <= 8; i++) {
      // eslint-disable-next-line no-undef
      val = (val << BigInt(8)) | BigInt(bytes[i]);
    }
    return Number(val);
  }
  return null;
}